import { scopeOf, type CurrentUser } from '@/auth/permissions'
import { request } from '@/lib/api'

/**
 * Активности записи — звонки, задачи, заметки (`/sales/activities/*`, тикет #22).
 *
 * Тип сущности здесь — **короткий код** (`deal`, `lead`, `counterparty`), а не имя каталога
 * списков (`sales.deal`): так их хранит `Activity.EntityType`. Комментарии (`/entities/...`)
 * — наоборот, по имени каталога. Два словаря для одной сущности названы в CLAUDE.md;
 * перепутать их значит получить пустую ленту, которая выглядит как «ничего не было».
 */
export type ActivityEntityType = 'lead' | 'deal' | 'counterparty' | 'contact' | 'invoice'

export interface Activity {
  id: string
  /** `call` | `email` | `meeting` | `task` | `note`. */
  type: string
  /** `planned` | `in_progress` | `done` | `cancelled`. */
  status: string
  subject: string
  body: string | null
  entityType: string
  entityId: string
  counterpartyId: string | null
  assigneeEmployeeId: string | null
  direction: string | null
  durationSeconds: number | null
  scheduledAt: string | null
  dueAt: string | null
  completedAt: string | null
  /** Считает сервер: «срок прошёл» — вопрос о текущем времени, а часы у клиента свои. */
  overdue: boolean
  createdAt: string
}

const BASE = '/sales/activities'

/**
 * Может ли пользователь записать звонок или задачу (право `sales.activity:create`, V63).
 * Запись на карточке дополнительно требует, чтобы карточка была видна, — раз она открыта,
 * это уже так.
 */
export function mayLogActivity(user: CurrentUser | null): boolean {
  return scopeOf(user, 'sales.activity', 'create') !== 'denied'
}

/**
 * Показывать ли «Выполнено» у задачи. Сервер решает по исполнителю в области
 * `sales.activity:update`: `own` — только своя (или ничья — тогда по праву на запись),
 * `department` — отдел, чей состав клиент не знает, поэтому кнопка показывается, а
 * чужой отдел получит 404. Кнопка, гарантированно ведущая к отказу, учит отказы
 * игнорировать.
 */
export function mayCompleteActivity(user: CurrentUser | null, activity: Activity): boolean {
  const scope = scopeOf(user, 'sales.activity', 'update')
  if (scope === 'denied') return false
  if (scope !== 'own') return true
  return activity.assigneeEmployeeId == null || activity.assigneeEmployeeId === user?.employeeId
}

/**
 * Активности записи, новые первыми.
 *
 * Сервер отдаёт последние `limit` по времени заведения, поэтому задача, заведённая раньше
 * сотни более поздних записей, сюда не попадёт. Для «следующего шага» это допустимая
 * граница: запись, у которой сто свежих активностей, не страдает от отсутствия шага.
 */
export function fetchActivities(
  entityType: ActivityEntityType,
  entityId: string,
  limit = 100,
  signal?: AbortSignal,
): Promise<Activity[]> {
  const query = new URLSearchParams({ entityType, entityId, limit: String(limit) })
  return request<Activity[]>(`${BASE}/feed?${query.toString()}`, { signal })
}

/** Задача со сроком; исполнитель — сотрудник, а не учётная запись. */
export function createTask(body: {
  entityType: ActivityEntityType
  entityId: string
  subject: string
  assigneeId: string | null
  dueAt: string | null
}): Promise<Activity> {
  return request<Activity>(`${BASE}/tasks`, { method: 'POST', body })
}

/** Звонок, который уже состоялся вне системы: менеджер набрал номер с телефона. */
export function logCall(body: {
  entityType: ActivityEntityType
  entityId: string
  subject: string
  direction: 'inbound' | 'outbound'
  durationSeconds: number | null
  employeeId: string
}): Promise<Activity> {
  return request<Activity>(`${BASE}/calls`, { method: 'POST', body })
}

export function completeActivity(activityId: string, outcome?: string): Promise<Activity> {
  return request<Activity>(`${BASE}/${activityId}/complete`, {
    method: 'POST',
    body: { outcome: outcome ?? null },
  })
}

/** Открытая — запланированная или взятая в работу; сделанная и отменённая шагом не являются. */
function isOpenActivity(activity: Activity): boolean {
  return activity.status === 'planned' || activity.status === 'in_progress'
}

/**
 * Ближайшая открытая задача — «следующий шаг».
 *
 * По сроку, а не по времени заведения: шаг — то, что надо сделать раньше всего. Задача без
 * срока идёт после задач со сроком — её «когда-нибудь» не обгоняет чьё-то «сегодня».
 */
export function nextStepOf(activities: Activity[]): Activity | null {
  const open = activities.filter((activity) => activity.type === 'task' && isOpenActivity(activity))
  open.sort((a, b) => {
    const left = a.dueAt ? Date.parse(a.dueAt) : Number.POSITIVE_INFINITY
    const right = b.dueAt ? Date.parse(b.dueAt) : Number.POSITIVE_INFINITY
    return left - right
  })
  return open[0] ?? null
}
